import { useEffect, useRef, useState } from 'react';
import ResponseModal from './ResponseModal';
import liff from '@line/liff';

function AfterpartyInvitation() {
  const [open, setOpen] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastPhase, setToastPhase] = useState('off'); // 'off' | 'on'
  const toastTimersRef = useRef([]);
  const [displayName, setDisplayName] = useState('');
  const [existingResponse, setExistingResponse] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        await liff.init({ 
          liffId: '2008036584-81vB1kbr',
          withLoginOnExternalBrowser: false
        });
        if (liff.isLoggedIn()) {
          try {
            const idToken = liff.getIDToken?.() || null;
            const profRes = await fetch(`/api/resolve_profile_afterparty`, {
              headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined
            });
            const profJson = await profRes.json();
            if (profJson.success && profJson.displayName) {
              setDisplayName(profJson.displayName || '');
            }
            const res = await fetch(`/api/get_afterparty_response_by_line_id`, {
              headers: idToken ? { Authorization: `Bearer ${idToken}` } : undefined
            });
            const json = await res.json();
            if (json.success && json.response) setExistingResponse(json.response);
          } catch (err) {
            console.error(err);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    init();
  }, []);

  const clearToastTimers = () => {
    toastTimersRef.current.forEach((id) => clearTimeout(id));
    toastTimersRef.current = [];
  };

  const showToast = () => {
    clearToastTimers();
    setToastVisible(true);
    setToastPhase('off');
    // Slide in after mount
    toastTimersRef.current.push(
      setTimeout(() => setToastPhase('on'), 10)
    );
    // Stay visible for 2.5s, then slide out
    toastTimersRef.current.push(
      setTimeout(() => setToastPhase('off'), 250 + 2500)
    );
    // After slide-out completes, unmount
    toastTimersRef.current.push(
      setTimeout(() => {
        setToastVisible(false);
      }, 250 + 2500 + 250)
    );
  };
  const handleSubmit = async (data) => {
    const idToken = liff.getIDToken?.() || null;
    const response = await fetch('/api/submit_afterparty', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
      },
      body: JSON.stringify(data)
    });
    
    const result = await response.json();
    if (result.success) {
      console.log('送信完了！', result);
      setOpen(false);
      showToast();
      try {
        const refreshIdToken = liff.getIDToken?.() || null;
        const res = await fetch(`/api/get_afterparty_response_by_line_id`, {
          headers: refreshIdToken ? { Authorization: `Bearer ${refreshIdToken}` } : undefined
        });
        const json = await res.json();
        if (json.success) setExistingResponse(json.response ?? null);
      } catch (err) {
        console.error(err);
      }
    }

  };

  return (
    <div>
      <div className="flex justify-center items-center min-h-screen bg-gray-100 py-4">
        <div
          className="relative w-full max-w-sm bg-white shadow-lg flex flex-col items-center p-4 pb-10" // 高さは内容に応じて伸縮
          style={{
            backgroundImage: 'url(/afterparty/background.avif)',
            backgroundSize: '100% auto',
            backgroundPosition: 'top center',
            backgroundRepeat: 'no-repeat', // 背景画像の繰り返しを防止
          }}
        >

          {/* Image Container with Arch Shape */}
          {/* メイン画像のコンテナサイズを相対的に調整 */}
          <div className="relative w-[80%] aspect-square mt-[42%] flex justify-center items-center">
            <div className="relative w-full h-full overflow-hidden" style={{ borderRadius: '40% 40% 0 0 / 40% 40% 0 0' }}>
              <img
                src="/afterparty/cover.avif"
                alt="Couple"
                className="w-full h-full object-cover" // object-coverでアスペクト比を維持しつつコンテナを埋める
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>
          </div>

          {/* Names */}
          <p className="text-center text-lg font-sans mt-2 uppercase tracking-widest">
            SATORU & AIMI
          </p>

          {/* Existing response banner (if any) */}
          {existingResponse && (
            <div className="w-full mt-6 px-3">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-gray-800 font-serifjp">
                <p className="mb-1">すでに以下の内容でご回答いただいています。</p>
                <p className="mb-2">回答内容を変更する場合は、お手数ですが再度ご回答ください。</p>
                <div className="grid grid-cols-1 gap-1">
                  <div>
                    <span className="text-gray-500">お名前：</span>
                    <span>{existingResponse.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">ご出欠：</span>
                    <span>{existingResponse.attendance === 'present' ? 'ご出席' : existingResponse.attendance === 'absent' ? 'ご欠席' : existingResponse.attendance}</span>
                  </div>
                  {existingResponse.allergies && (
                    <div>
                      <span className="text-gray-500">アレルギー：</span>
                      <span>{existingResponse.allergies}</span>
                    </div>
                  )}
                  {existingResponse.message && (
                    <div>
                      <span className="text-gray-500">コメント：</span>
                      <span>{existingResponse.message}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* INFORMATION Section */}
          <div className={`w-full mt-10 px-4 ${existingResponse ? 'mt-4' : ''}`}>
            <h2 className="text-center font-heading text-[#8f5c58] text-[18px] leading-none">
              INFORMATION
            </h2>
            <div className="mt-3 text-center text-[13px] text-gray-700 font-serifjp space-y-1">
              <p>日付：2025年11月2日 (日)</p>
              <p>時間：16:30 ~ 18:30 (受付開始 16:00)</p>
              <p>会場：ラシックアンジュール銀座</p>
              <p>会費：7,000円</p>
            </div>
          </div>

          {/* ACCESS Section */}
          <div className="w-full mt-10 px-4">
            <h2 className="text-center font-heading text-[#8f5c58] text-[18px] leading-none">
              ACCESS
            </h2>
            <div className="mt-3 text-center text-[13px] text-gray-700 font-serifjp space-y-1">
              <p>〒104-0061</p>
              <p>東京都中央区銀座2-3-6　銀座並木通りビル6F</p>
              <p>TEL: 03-6263-0509</p>
              <p>
                <a
                  href="https://le-chic-unjour.com/access"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[11px]"
                >
                  https://le-chic-unjour.com/access
                </a>
              </p>
            </div>
            <div className="mt-4">
              <div className="relative w-full overflow-hidden" style={{ paddingTop: '56.25%' }}>
                <iframe
                  title="ラシックアンジュール 地図"
                  src="https://www.google.com/maps?q=%E3%83%A9%E3%82%B7%E3%83%83%E3%82%AF%E3%82%A2%E3%83%B3%E3%82%B8%E3%83%A5%E3%83%BC%E3%83%AB%E9%8A%80%E5%BA%A7&hl=ja&z=14&output=embed"
                  className="absolute top-0 left-0 w-full h-full border-0 rounded-md"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
          {/* Gallery */}
          <div className="w-full mt-10 px-4" style={{ contentVisibility: 'auto', containIntrinsicSize: '1200px' }}>
            <h2 className="text-center font-heading text-[#8f5c58] text-[18px] leading-none">
              GALLERY
            </h2>
            <div className="mt-4 -mx-4 px-4 overflow-x-auto no-scrollbar">
              <div className="flex gap-3 w-max">
                {/* Column style groups with varied aspect ratios */}
                <div className="flex flex-col gap-3">
                  <div className="w-[155px] aspect-[5/6] overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/vert1.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                  <div className="w-[155px] aspect-[5/6] overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/vert4.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="w-[180px] aspect-[2/1] overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/hori7.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                  <div className="w-[180px] aspect-square overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/square4.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                  <div className="w-[180px] aspect-[2/1] overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/hori8.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="w-[155px] aspect-[5/6] overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/vert8.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                  <div className="w-[155px] aspect-[5/6] overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/vert6.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="w-[180px] aspect-[2/1] overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/hori1.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                  <div className="w-[180px] aspect-square overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/vert3.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                  <div className="w-[180px] aspect-[2/1] overflow-hidden rounded-lg bg-gray-100">
                    <img src="/gallery/hori4.avif" alt="gallery" className="w-full h-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-center text-[13px] text-gray-700 font-serifjp space-y-1 mb-10">
              <p>お手数ではございますが</p>
              <p>ご都合の程を10月1日迄に</p>
              <p>ご一報賜りますようお願い申し上げます</p>
            </div>
        </div>
      </div>
      <div className="fixed bottom-2 left-1/2 -translate-x-1/2 transform z-50 w-full max-w-sm">
        {toastVisible && (
          <div className="mb-2 w-full flex justify-end overflow-hidden">
            <div
              className={`w-[120px] h-[40px] flex items-center justify-center bg-[#E8C547] text-[#5C4033] text-sm font-sans rounded-l-[20px] rounded-r-none shadow transition-transform duration-[250ms] transform ${
                toastPhase === 'on' ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              回答完了 ✓
            </div>
          </div>
        )}
        <div className="px-9">
          <button
            disabled={true}
            type="button"
            onClick={() => setOpen(true)}
            className="w-full max-w-sm rounded-full bg-[#8f5c58] text-white text-base font-sans py-3 shadow-lg"
          >
            回答は締め切られました
          </button>
        </div>
      </div>
      <ResponseModal 
        open={open} 
        onClose={() => setOpen(false)} 
        onSubmit={handleSubmit}
        defaultValues={(function computeDefaultValues() {
          const kanjiRegex = /^[\u4e00-\u9faf]+$/;
          const spaceRegex = /[\s\u3000]+/;
          if (existingResponse) {
            const nameParts = (existingResponse.name || '').trim().split(spaceRegex);
            const lastName = nameParts[0] || '';
            const firstName = nameParts[1] || '';
            return {
              attendance: existingResponse.attendance || '',
              lastName,
              firstName,
              allergy: existingResponse.allergies || '',
              comment: existingResponse.message || ''
            };
          }
          if (displayName !== '') {
            const parts = displayName.trim().split(spaceRegex);
            if (parts.length === 2 && kanjiRegex.test(parts[0]) && kanjiRegex.test(parts[1])) {
              return { lastName: parts[0], firstName: parts[1] };
            }
          }
          return undefined;
        })()}
      />
      
    </div>
  );
}

export default AfterpartyInvitation;
